// src/app/components/antenna/ask-upmode-bar.tsx
//
// AskUpmodeBar is the page-level conversational entry point. Lives at the top of the
// page content (not sticky; it scrolls away). It is a live input: type a
// question and press Enter (or the send button) to open the AI sidebar with
// that question; a starter chip opens it with that text prefilled. Same
// assistant, same thread, same ⌘K as every other ask-affordance.
import { useState } from "react";
import { Sparkles, ArrowRight } from "lucide-react";

interface Starter {
  /** Short, orienting text shown on the chip. */
  label: string;
  /** Brand-grounded prompt actually sent to the assistant, so it doesn't free-associate. */
  query: (brand: string) => string;
}

interface AskUpmodeBarProps {
  brandName?: string;
  /** Same doorway as everywhere else, opens the AI sidebar, optionally prefilled. */
  onAsk: (query?: string) => void;
  starters?: Starter[];
  /** Override the input placeholder (defaults to the brand-grounded prompt). */
  placeholder?: string;
}

// Labels stay orienting; the query carries the brand context so the assistant
// answers about this brand instead of inventing a topic from a bare phrase.
const DEFAULT_STARTERS: Starter[] = [
  { label: "Summarize what matters most", query: (b) => `Summarize the most important things I should know right now about how AI describes ${b}.` },
  { label: "Where should I focus first?", query: (b) => `Based on how AI currently describes ${b}, where should I focus first to improve?` },
  { label: "How does AI see us overall?", query: (b) => `Give me an overall read on how AI describes ${b} today.` },
];

// Shared AI identity — keep this gradient identical to the sidebar orb at every size.
function MiniOrb() {
  return (
    <span
      style={{
        width: 13,
        height: 13,
        borderRadius: 9999,
        background: "linear-gradient(135deg, var(--spectral-violet), var(--signal-blue))",
        flexShrink: 0,
      }}
    />
  );
}

export function AskUpmodeBar({ brandName, onAsk, starters = DEFAULT_STARTERS, placeholder }: AskUpmodeBarProps) {
  const brand = brandName?.trim() || "your brand";
  const [value, setValue] = useState("");

  // Submit the typed question. An empty value still opens the sidebar (same as
  // the old doorway behavior) so the bar never feels inert; a typed value is
  // sent through verbatim. Clear after handing off.
  const submit = () => {
    const q = value.trim();
    onAsk(q.length ? q : undefined);
    setValue("");
  };

  const hasText = value.trim().length > 0;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <style>{`
        .ask-upmode-bar:hover { border-color: var(--spectral-violet); }
        .ask-upmode-bar:focus-within { border-color: var(--spectral-violet); }
        .ask-upmode-input::placeholder { color: var(--muted-foreground); }
        .ask-upmode-chip:hover { border-color: var(--spectral-violet); color: var(--spectral-violet); }
        .ask-upmode-send:hover { opacity: 0.9; }
      `}</style>

      {/* Row 1 — the bar */}
      <form
        className="ask-upmode-bar"
        onSubmit={(e) => { e.preventDefault(); submit(); }}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 13,
          background: "var(--card)",
          border: "1px solid var(--border)",
          borderRadius: 14,
          padding: "13px 15px",
          boxShadow: "0 1px 2px rgba(13,13,15,.04), 0 12px 32px -18px rgba(126,95,224,.4)",
          transition: "border-color var(--duration-base) var(--ease-standard)",
        }}
      >
        {/* Orb */}
        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            width: 32,
            height: 32,
            borderRadius: 10,
            background:
              "linear-gradient(135deg, var(--spectral-violet) 0%, var(--spectral-violet) 62%, var(--signal-blue) 155%)",
            boxShadow: "var(--shadow-spectral-violet-glow)",
            color: "#fff",
            flexShrink: 0,
          }}
        >
          <Sparkles size={15} color="#fff" />
        </span>

        {/* Input */}
        <input
          className="ask-upmode-input"
          type="text"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder={placeholder ?? `Ask anything about how AI sees ${brand}: "How do we earn a top-3 ranking?"`}
          aria-label={`Ask anything about how AI sees ${brand}`}
          style={{
            flex: 1,
            minWidth: 0,
            fontSize: 14,
            color: "var(--foreground)",
            background: "transparent",
            border: "none",
            outline: "none",
            padding: 0,
          }}
        />

        {/* ⌘K hint when empty, send button once there's text */}
        {hasText ? (
          <button
            type="submit"
            className="ask-upmode-send"
            aria-label="Ask"
            style={{
              flexShrink: 0,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              width: 28,
              height: 28,
              borderRadius: 8,
              background: "linear-gradient(135deg, var(--spectral-violet), var(--signal-blue))",
              color: "#fff",
              border: "none",
              cursor: "pointer",
              transition: "opacity var(--duration-base) var(--ease-standard)",
            }}
          >
            <ArrowRight size={15} color="#fff" />
          </button>
        ) : (
          <span
            style={{
              flexShrink: 0,
              fontSize: 11,
              fontWeight: 600,
              color: "var(--muted-foreground)",
              border: "1px solid var(--border)",
              borderRadius: 6,
              padding: "3px 8px",
            }}
          >
            ⌘K
          </span>
        )}
      </form>

      {/* Row 2 — starters */}
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
        <span style={{ fontSize: 12, color: "var(--muted-foreground)" }}>Not sure where to start?</span>
        {starters.map((s, i) => (
          <button
            key={i}
            className="ask-upmode-chip"
            onClick={() => onAsk(s.query(brand))}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 7,
              height: 32,
              padding: "0 13px",
              borderRadius: 9999,
              background: "var(--card)",
              border: "1px solid var(--border)",
              fontSize: 12,
              fontWeight: 500,
              color: "var(--foreground)",
              cursor: "pointer",
              transition:
                "border-color var(--duration-base) var(--ease-standard), color var(--duration-base) var(--ease-standard)",
            }}
          >
            <MiniOrb />
            {s.label}
          </button>
        ))}
      </div>
    </div>
  );
}
